/**
 * ContactList Component
 * Displays paginated list of contacts with search, filtering, and multi-select
 */

import React, { useState, useEffect, useMemo } from "react";
import SearchBar from "../common/SearchBar";
import { Link, useSearchParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { getContacts } from "../../services/contacts.service";
import {
  getAgents,
  getSupportAgentById,
} from "../../services/supportTasksDb.service";
import { useAuth } from "../../contexts/AuthContext";
import { isReadonlyRole } from "../../utils/roleUtils";
import { logError } from "../../utils/errorLogger";
import {
  QUERY_KEYS,
  DEFAULT_PAGE_SIZE,
  LIFECYCLE_STAGES,
} from "../../utils/constants";
import { formatDate, formatPhoneNumber } from "../../utils/formatters";
import Button from "../common/Button";
import LoadingSpinner from "../common/LoadingSpinner";
import Card from "../ui/Card";
import AddToSegmentDropdown from "../segments/AddToSegmentDropdown";
import CreateContactModal from "./CreateContactModal";
import EmailSuppressionBadge from "./EmailSuppressionBadge";
import ContactOptOutBadges from "./ContactOptOutBadges";
import type { UUID } from "../../types/common.types";
import type { SupportAgent } from "../../types/supportTasks.types";
import type { ContactFilters } from "../../types/contact.types";

/**
 * Render the "Error loading contacts" panel and capture a structured
 * diagnostic to localStorage + console so the next occurrence is
 * self-documenting. Operators reported intermittent failures on PROD
 * that they couldn't reproduce and didn't have DevTools open for; this
 * panel surfaces the failing search-term shape, PostgREST error code,
 * and a one-click "Copy diagnostic" the user can paste into a ticket.
 *
 * 2026-06-02 hotfix instrumentation.
 */
const ContactsErrorPanel: React.FC<{
  error: unknown;
  user: { id?: string; role?: string } | null | undefined;
}> = ({ error, user }) => {
  const [copied, setCopied] = React.useState(false);
  const diagnostic = React.useMemo(() => {
    const err = error as Error & {
      code?: string;
      details?: string;
      hint?: string;
      __diagnostic?: Record<string, unknown>;
    };
    return {
      ts: new Date().toISOString(),
      url: window.location.pathname + window.location.search,
      user_id: user?.id ?? null,
      user_role: user?.role ?? null,
      user_agent: navigator.userAgent,
      error_message: err?.message ?? String(error),
      postgrest_code: err?.code ?? null,
      postgrest_details: err?.details ?? null,
      postgrest_hint: err?.hint ?? null,
      context: err?.__diagnostic ?? null,
    };
  }, [error, user]);

  React.useEffect(() => {
    // Persist to localStorage via the shared error logger and also
    // print a labeled console line so the user can paste it directly.
    logError({
      type: "ContactsLoadError",
      error: diagnostic.error_message,
      timestamp: diagnostic.ts,
      url: diagnostic.url,
      userAgent: diagnostic.user_agent,
      userId: diagnostic.user_id ?? undefined,
      userRole: diagnostic.user_role ?? undefined,
      additionalData: {
        postgrest_code: diagnostic.postgrest_code,
        postgrest_details: diagnostic.postgrest_details,
        postgrest_hint: diagnostic.postgrest_hint,
        context: diagnostic.context,
      },
    });
    // eslint-disable-next-line no-console
    console.error("[ContactsLoadError]", diagnostic);
  }, [diagnostic]);

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(JSON.stringify(diagnostic, null, 2));
      setCopied(true);
      window.setTimeout(() => setCopied(false), 2000);
    } catch {
      // Clipboard API can be blocked; fall through silently
    }
  };

  return (
    <Card variant="outlined" padding="md" className="border-red-200 bg-red-50">
      <div className="space-y-2">
        <p className="text-red-800 font-medium">Error loading contacts</p>
        <p className="text-red-700 text-sm">
          {diagnostic.error_message || "Unknown error"}
          {diagnostic.postgrest_code
            ? ` (code ${diagnostic.postgrest_code})`
            : ""}
        </p>
        <div className="flex items-center gap-3 pt-1">
          <button
            type="button"
            onClick={handleCopy}
            className="text-xs px-3 py-1 rounded border border-red-300 text-red-800 hover:bg-red-100"
          >
            {copied ? "Copied" : "Copy diagnostic info"}
          </button>
          <span className="text-xs text-red-700">
            Please send the copied info to support so we can diagnose.
          </span>
        </div>
      </div>
    </Card>
  );
};

const ContactList: React.FC = () => {
  const { user } = useAuth();
  const [page, setPage] = useState(1);
  const [searchQuery, setSearchQuery] = useState("");
  // Explicit-submit search: fire only on Enter / Search button (no live querying).
  const handleSearch = (q: string) => {
    setSearchQuery(q);
    setPage(1);
  };
  const [selectedIds, setSelectedIds] = useState<Set<UUID>>(new Set());
  const [showCreateModal, setShowCreateModal] = useState(false);
  // Filter states
  const [lifecycleStage, setLifecycleStage] = useState<string>("");
  // Deep-link support: /contacts?agent=<uuid>&from=agent arrives from the Agent
  // Management detail card. Seed the filter from the URL so the link lands on a
  // pre-filtered list, and remember where to send the operator back to.
  const [searchParams] = useSearchParams();
  const agentParam = searchParams.get("agent") ?? "";
  const cameFromAgentPanel = searchParams.get("from") === "agent";
  const [agentFilter, setAgentFilter] = useState<string>(agentParam);
  const [emailSuppressionStatus, setEmailSuppressionStatus] = useState<
    NonNullable<ContactFilters["email_suppression_status"]> | ""
  >("");

  // Check if user is support role. Compare against the exact stored role values — do NOT strip
  // underscores, or support_admin/support_agent silently fail the match.
  const isSupportRole =
    user?.role &&
    ["support_admin", "support_agent"].includes(user.role.toLowerCase());
  // Read-only viewer (owner 2026-11-03): search and open contacts only -- no create, no selection, no segment adds.
  const readOnly = isReadonlyRole(user?.role);

  // Default to 'customer' lifecycle stage for support roles.
  // EXCEPTION: when deep-linked from the Agent Management detail card, leave the
  // stage unset. That card shows a count of ALL contacts assigned to the agent, so
  // silently adding a lifecycle filter here would render a shorter list than the
  // number the operator just clicked — which reads as a bug in the count.
  useEffect(() => {
    if (isSupportRole && lifecycleStage === "" && !cameFromAgentPanel) {
      setLifecycleStage("customer");
    }
  }, [isSupportRole, lifecycleStage, cameFromAgentPanel]);

  // Fetch support agents for filter dropdown
  const { data: agents = [] } = useQuery({
    queryKey: ["support-agents"],
    queryFn: getAgents,
    enabled: isSupportRole, // Only fetch for support roles
  });

  // Name of the agent this list is filtered to, for the deep-link banner.
  // The `agents` query above is enabled ONLY for support roles, so an admin or
  // superadmin following the deep link would otherwise see "the selected agent"
  // instead of a name. Fall back to a direct by-id lookup, which every role that
  // can reach this page may make. Degrades to the generic label if it fails, so
  // the Back link is never blocked on it.
  //
  // ⚠️ This hook MUST stay above the `isLoading` / `error` early returns below.
  // Placing it after them changes the hook count between renders and throws
  // React error #310 ("rendered more hooks than during the previous render"),
  // which crashes the whole page — caught in QA render verification 2026-09-12.
  const agentFromList = agentFilter
    ? (agents.find((a: SupportAgent) => a.id === agentFilter)?.name ?? null)
    : null;
  const { data: deepLinkAgent } = useQuery({
    queryKey: ["support-agent", agentFilter],
    queryFn: () => getSupportAgentById(agentFilter as UUID),
    enabled: !!agentFilter && !agentFromList,
  });
  const filteredAgentName = agentFromList ?? deepLinkAgent?.name ?? null;

  // Build filters object
  const filters = useMemo(
    () => ({
      ...(searchQuery && { search: searchQuery }),
      ...(lifecycleStage && { lifecycle_stage: lifecycleStage }),
      ...(agentFilter && { assigned_agent_id: agentFilter }),
      ...(emailSuppressionStatus && {
        email_suppression_status: emailSuppressionStatus,
      }),
    }),
    [searchQuery, lifecycleStage, agentFilter, emailSuppressionStatus],
  );

  const { data, isLoading, error } = useQuery({
    queryKey: [
      QUERY_KEYS.CONTACTS,
      page,
      searchQuery,
      lifecycleStage,
      agentFilter,
      emailSuppressionStatus,
    ],
    queryFn: () =>
      getContacts(
        {
          page,
          limit: DEFAULT_PAGE_SIZE,
          sortBy: "created_at",
          sortOrder: "desc",
        },
        Object.keys(filters).length > 0 ? filters : undefined,
      ),
    // Read-only viewer (owner 2026-11-04): search only -- no browse list (it would read the contacts table directly).
    enabled: !readOnly || !!searchQuery,
  });

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  if (error) {
    return <ContactsErrorPanel error={error} user={user} />;
  }

  const contacts = data?.data || [];

  // Selection handlers
  const toggleSelect = (id: UUID) => {
    const newSelected = new Set(selectedIds);
    if (newSelected.has(id)) {
      newSelected.delete(id);
    } else {
      newSelected.add(id);
    }
    setSelectedIds(newSelected);
  };

  const toggleSelectAll = () => {
    if (selectedIds.size === contacts.length && contacts.length > 0) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(contacts.map((c: any) => c.id)));
    }
  };

  const clearSelection = () => {
    setSelectedIds(new Set());
  };

  return (
    <div className="space-y-4">
      {/* Deep-link banner: arrived here from the Agent Management detail card.
          Gives the operator an explicit way back — browser-back would also work,
          but the panel is an overlay whose open state isn't in the URL, so a
          plain back would drop them on the task list with the panel closed.

          The link deliberately returns to the support task DASHBOARD, not to the
          agent list: the panel is a means of getting here, not a place to go back
          to, and an operator who came from the dashboard's Agent Activity table
          never saw the list at all. The label says so plainly — it previously read
          "Back to Support Agents", which promised the agent list and delivered the
          dashboard. */}
      {cameFromAgentPanel && agentParam && (
        <div className="flex items-center justify-between gap-4 px-4 py-3 bg-primary-50 border border-primary-100 rounded-lg">
          <div className="text-sm text-primary-900">
            Showing contacts assigned to{" "}
            <span className="font-medium">
              {filteredAgentName ?? "the selected agent"}
            </span>
          </div>
          <Link
            to="/support-tasks"
            className="inline-flex items-center gap-1.5 text-sm font-medium text-primary-700 hover:text-primary-900 whitespace-nowrap"
          >
            <svg
              className="w-4 h-4"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M15 19l-7-7 7-7"
              />
            </svg>
            Back to Support Tasks
          </Link>
        </div>
      )}

      {/* Search and Actions Row — explicit submit (Enter / Search button), no live querying */}
      <div className="flex items-center justify-between gap-4">
        <SearchBar
          placeholder="Search contacts..."
          initialValue={searchQuery}
          onSearch={handleSearch}
          className="flex-1 max-w-md"
        />
        {!readOnly && (
          <div className="flex items-center gap-2">
            <Button
              variant="primary"
              size="sm"
              onClick={() => setShowCreateModal(true)}
            >
              + Create Contact
            </Button>
            <AddToSegmentDropdown
              selectedContactIds={Array.from(selectedIds)}
              onSuccess={clearSelection}
            />
          </div>
        )}
      </div>

      {/* Filters Row (not for the read-only viewer: search only) */}
      {!readOnly && (
        <div className="flex flex-wrap gap-4 items-center">
          {/* Lifecycle Stage Filter */}
          <div className="flex items-center gap-2">
            <label className="text-sm font-medium text-gray-600">Type:</label>
            <select
              value={lifecycleStage}
              onChange={(e) => {
                setLifecycleStage(e.target.value);
                setPage(1);
              }}
              className="px-3 py-1.5 text-sm border border-gray-300 rounded-lg"
            >
              <option value="">All Types</option>
              {LIFECYCLE_STAGES.map((stage) => (
                <option key={stage.value} value={stage.value}>
                  {stage.label}
                </option>
              ))}
            </select>
          </div>

          {/* Agent Filter - Only show for support roles */}
          {isSupportRole && agents.length > 0 && (
            <div className="flex items-center gap-2">
              <label className="text-sm font-medium text-gray-600">
                Assigned Agent:
              </label>
              <select
                value={agentFilter}
                onChange={(e) => {
                  setAgentFilter(e.target.value);
                  setPage(1);
                }}
                className="px-3 py-1.5 text-sm border border-gray-300 rounded-lg"
              >
                <option value="">All Agents</option>
                {agents.map((agent: SupportAgent) => (
                  <option key={agent.id} value={agent.id}>
                    {agent.name}
                  </option>
                ))}
              </select>
            </div>
          )}

          {/* Email Status Filter — backed by idx_contacts_is_email_suppressed */}
          <div className="flex items-center gap-2">
            <label className="text-sm font-medium text-gray-600">
              Email status:
            </label>
            <select
              value={emailSuppressionStatus}
              onChange={(e) => {
                setEmailSuppressionStatus(
                  e.target.value as typeof emailSuppressionStatus,
                );
                setPage(1);
              }}
              className="px-3 py-1.5 text-sm border border-gray-300 rounded-lg"
            >
              <option value="">All</option>
              <option value="active">Active (deliverable)</option>
              <option value="any_suppressed">Any suppressed</option>
              <option value="unsubscribed">Unsubscribed</option>
              <option value="hard_bounced">Hard bounced</option>
              <option value="soft_bounce_threshold">Soft bounced</option>
              <option value="spam_reported">Spam reported</option>
              <option value="manual">Manual suppression</option>
            </select>
          </div>

          {/* Tier 1.5 saved-filter link — one-click access to the
            "needs review" work queue per audit V2 verdict. */}
          {emailSuppressionStatus !== "any_suppressed" && (
            <button
              type="button"
              onClick={() => {
                setEmailSuppressionStatus("any_suppressed");
                setPage(1);
              }}
              className="text-sm text-red-700 hover:text-red-900 underline font-medium"
              title="Show all contacts whose email address is currently suppressed (bounce / unsubscribe / spam / manual)"
            >
              Email suppressed — needs review
            </button>
          )}

          {/* Clear Filters */}
          {(lifecycleStage || agentFilter || emailSuppressionStatus) && (
            <button
              onClick={() => {
                setLifecycleStage(isSupportRole ? "customer" : "");
                setAgentFilter("");
                setEmailSuppressionStatus("");
                setPage(1);
              }}
              className="text-sm text-gray-500 hover:text-gray-700 underline"
            >
              Clear Filters
            </button>
          )}
        </div>
      )}

      {/* Selection indicator */}
      {selectedIds.size > 0 && (
        <div className="flex items-center gap-2 px-4 py-2 bg-primary-50 rounded-lg">
          <span className="text-sm text-primary-700">
            {selectedIds.size} contact{selectedIds.size !== 1 ? "s" : ""}{" "}
            selected
          </span>
          <button
            onClick={clearSelection}
            className="text-sm text-primary-600 hover:text-primary-800 underline"
          >
            Clear selection
          </button>
        </div>
      )}

      {/* Searched-page notices (search_contacts_list, migration 770). Only a searched page carries these
          flags; the unsearched list never shows them. */}
      {searchQuery && data?.searchRefused && (
        <p
          className="text-sm text-gray-600"
          data-testid="contacts-search-refused"
        >
          {data.searchRefused === "too_broad"
            ? "That is too broad to search. Type more of the e-mail address (at least 3 characters before the @), not just the domain."
            : "Type at least 3 letters of a name, or an e-mail address, phone number or member number."}
        </p>
      )}
      {searchQuery && data?.totalCapped && (
        <p
          className="text-sm text-gray-600"
          data-testid="contacts-search-capped"
        >
          More than 1,000 contacts match, so this list shows only 1,000 of them
          (sorted by name) and the person you want may not be in it. Add a last
          name, e-mail address or phone number to narrow the search.
        </p>
      )}
      {searchQuery && data?.incomplete && (
        <p
          className="text-sm text-amber-700"
          data-testid="contacts-search-incomplete"
        >
          This search is broad for the selected filters, so some matching
          contacts may not be listed. Add a last name, e-mail address or phone
          number to narrow the search.
        </p>
      )}

      <Card>
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 py-3 w-10">
                  {!readOnly && (
                    <input
                      type="checkbox"
                      checked={
                        selectedIds.size === contacts.length &&
                        contacts.length > 0
                      }
                      onChange={toggleSelectAll}
                      className="w-4 h-4 text-primary-600 border-gray-300 rounded focus:ring-primary-500"
                    />
                  )}
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                  Name
                </th>
                {readOnly && (
                  <>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                      Membership
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                      Member #
                    </th>
                  </>
                )}
                {!readOnly && (
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                    Email
                  </th>
                )}
                {!readOnly && (
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                    Phone
                  </th>
                )}
                {isSupportRole && (
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                    Type
                  </th>
                )}
                {!readOnly && (
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                    Created
                  </th>
                )}
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {contacts.length === 0 ? (
                <tr>
                  <td
                    colSpan={readOnly ? 4 : isSupportRole ? 6 : 5}
                    className="px-6 py-12 text-center text-gray-500"
                  >
                    {readOnly && !searchQuery
                      ? "Search by name, email, phone or member number to check a membership."
                      : "No contacts found"}
                  </td>
                </tr>
              ) : (
                contacts.map((contact: any) => (
                  <tr
                    key={contact.id}
                    className={`hover:bg-gray-50 ${selectedIds.has(contact.id) ? "bg-primary-50" : ""}`}
                  >
                    <td className="px-4 py-4">
                      {!readOnly && (
                        <input
                          type="checkbox"
                          checked={selectedIds.has(contact.id)}
                          onChange={() => toggleSelect(contact.id)}
                          className="w-4 h-4 text-primary-600 border-gray-300 rounded focus:ring-primary-500"
                        />
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      {/* Clickable contact name. Fallback to email then a
                          generic placeholder when both first_name and
                          last_name are empty — the Link otherwise renders
                          as invisible whitespace and the row appears
                          unclickable (audit 2026-05-20). */}
                      {readOnly ? (
                        // Read-only viewer (owner 2026-11-04): search only -- no contact detail page.
                        <span
                          className="text-sm font-medium text-gray-900"
                          data-testid="contact-name-readonly"
                        >
                          {contact.first_name || contact.last_name
                            ? `${contact.first_name ?? ""} ${contact.last_name ?? ""}`.trim()
                            : "(no name)"}
                        </span>
                      ) : (
                        <>
                          <Link
                            to={`/contacts/${contact.id}`}
                            className="text-sm font-medium text-blue-600 hover:text-blue-800 hover:underline"
                          >
                            {contact.first_name || contact.last_name
                              ? `${contact.first_name ?? ""} ${contact.last_name ?? ""}`.trim()
                              : contact.email || "(no name)"}
                          </Link>
                          <ContactOptOutBadges
                            contact={contact}
                            compact
                            className="mt-1"
                          />
                        </>
                      )}
                    </td>
                    {readOnly && (
                      <>
                        <td
                          className="px-6 py-4 whitespace-nowrap text-sm"
                          data-testid="contact-membership-status"
                        >
                          {contact.member_status ? (
                            <span
                              className={`px-2 py-0.5 rounded-full text-xs font-medium ${
                                String(contact.member_status).toLowerCase() ===
                                "active"
                                  ? "bg-green-100 text-green-800"
                                  : "bg-gray-100 text-gray-700"
                              }`}
                            >
                              {contact.member_status}
                            </span>
                          ) : (
                            <span className="text-gray-400">Not a member</span>
                          )}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700 font-mono">
                          {contact.membership_id || "-"}
                        </td>
                      </>
                    )}
                    {!readOnly && (
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        <div className="flex items-center gap-2">
                          <span>{contact.email || "-"}</span>
                          {contact.email && (
                            <EmailSuppressionBadge
                              email={contact.email}
                              isEmailSuppressed={contact.is_email_suppressed}
                              suppressionReason={
                                contact.email_suppression_reason
                              }
                              suppressionDetail={
                                contact.email_suppression_detail
                              }
                              suppressionUpdatedAt={
                                contact.email_suppression_updated_at
                              }
                              compact
                            />
                          )}
                        </div>
                      </td>
                    )}
                    {!readOnly && (
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {contact.phone_number
                          ? formatPhoneNumber(contact.phone_number)
                          : "-"}
                      </td>
                    )}
                    {isSupportRole && (
                      <td className="px-6 py-4 whitespace-nowrap text-sm">
                        {contact.lifecycle_stage && (
                          <span
                            className={`px-2 py-0.5 rounded-full text-xs font-medium ${
                              contact.lifecycle_stage === "customer"
                                ? "bg-green-100 text-green-700"
                                : contact.lifecycle_stage === "prospect"
                                  ? "bg-blue-100 text-blue-700"
                                  : contact.lifecycle_stage === "lead"
                                    ? "bg-yellow-100 text-yellow-700"
                                    : contact.lifecycle_stage === "inactive"
                                      ? "bg-gray-100 text-gray-700"
                                      : "bg-purple-100 text-purple-700"
                            }`}
                          >
                            {LIFECYCLE_STAGES.find(
                              (s) => s.value === contact.lifecycle_stage,
                            )?.label || contact.lifecycle_stage}
                          </span>
                        )}
                      </td>
                    )}
                    {!readOnly && (
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {formatDate(contact.created_at)}
                      </td>
                    )}
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {data?.total && data.total > DEFAULT_PAGE_SIZE && (
          <div className="px-6 py-4 border-t border-gray-200 flex items-center justify-between">
            <div className="text-sm text-gray-700">
              Showing {(page - 1) * DEFAULT_PAGE_SIZE + 1} to{" "}
              {Math.min(page * DEFAULT_PAGE_SIZE, data.total)} of{" "}
              {data.totalCapped
                ? `${data.total.toLocaleString()}+`
                : data.incomplete
                  ? `at least ${data.total.toLocaleString()}`
                  : data.total}
            </div>
            <div className="flex space-x-2">
              <Button
                variant="secondary"
                size="sm"
                onClick={() => setPage((p) => p - 1)}
                disabled={page === 1}
              >
                Previous
              </Button>
              <Button
                variant="secondary"
                size="sm"
                onClick={() => setPage((p) => p + 1)}
                disabled={page * DEFAULT_PAGE_SIZE >= data.total}
              >
                Next
              </Button>
            </div>
          </div>
        )}
      </Card>

      {!readOnly && (
        <CreateContactModal
          isOpen={showCreateModal}
          onClose={() => setShowCreateModal(false)}
        />
      )}
    </div>
  );
};

export default ContactList;
